import { Fragment, useState, type KeyboardEvent, type ReactNode } from 'react'

import { formatInteger, formatPercent, formatUsd } from '../../demo/format'
import { useProductLens } from '../../demo/selectors'
import type { AnalyticsLens, AnalyticsRangeKey, ProductLensRow } from '../../demo/types'
import { Card, Icon, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Term } from '../../ui'
import { acosRedline, lensColumns, lensColumnTermKeys, lensCopy } from './lensCopy'
import { formatDelta } from './lensMath'
import { ProductAdRows } from './ProductAdRows'
import styles from './ProductTable.module.css'

interface ProductTableProps {
  range: AnalyticsRangeKey
  lens: AnalyticsLens
  action: ReactNode
}

interface ProductRowProps {
  row: ProductLensRow
  lens: AnalyticsLens
  expanded: boolean
  onToggle: () => void
}

function ProductRow({ row, lens, expanded, onToggle }: ProductRowProps) {
  const openFromKeyboard = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }
  const deltaClass = row.deltaPrev === null ? styles.muted : row.deltaPrev >= 0 ? styles.up : styles.down

  return (
    <TableRow
      className={styles.productRow}
      clickable
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${lensCopy.expandProduct}：${row.product}`}
      onClick={onToggle}
      onKeyDown={openFromKeyboard}
    >
      <TableCell className={styles.expandCell}>
        <Icon className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`} name="chevron-right" size={17} strokeWidth={2} />
      </TableCell>
      <TableCell>
        <div className={styles.product}>
          <img className={styles.thumb} src={`/${row.image}`} alt={row.product} />
          <div>
            <p className={styles.productName}>
              {row.product}
              <span
                className={`${styles.healthDot} ${row.linkOk ? styles.healthOk : styles.healthInvalid}`}
                title={row.linkOk ? lensCopy.linkOk : lensCopy.linkInvalid}
              />
            </p>
            {!row.linkOk ? <p className={styles.linkInvalid}>{lensCopy.linkInvalidShort}</p> : null}
          </div>
        </div>
      </TableCell>
      <TableCell>{lensCopy.adCount(row.ads, row.adsLive)}</TableCell>
      {lens === 'sales' ? (
        <>
          <TableCell numeric>{formatInteger(row.units)}</TableCell>
          <TableCell numeric>{row.aov === null ? '—' : formatUsd(row.aov, 2)}</TableCell>
          <TableCell numeric>{formatUsd(row.revenue)}</TableCell>
          <TableCell numeric>{formatUsd(row.spend)}</TableCell>
          <TableCell numeric className={row.acos !== null && row.acos > acosRedline ? styles.overRedline : ''}>{row.acos === null ? '—' : formatPercent(row.acos)}</TableCell>
        </>
      ) : (
        <>
          <TableCell numeric>{formatInteger(row.reach)}</TableCell>
          <TableCell numeric>{formatInteger(row.impressions)}</TableCell>
          <TableCell numeric>{row.visits === null ? '—' : formatInteger(row.visits)}</TableCell>
          <TableCell numeric>{formatUsd(row.spend)}</TableCell>
          <TableCell numeric>{row.cpm === null ? '—' : formatUsd(row.cpm, 2)}</TableCell>
        </>
      )}
      <TableCell><span className={deltaClass}>{formatDelta(row.deltaPrev)}</span></TableCell>
    </TableRow>
  )
}

export function ProductTable({ range, lens, action }: ProductTableProps) {
  const view = useProductLens(range, lens)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const totalAds = view.rows.reduce((sum, row) => sum + row.ads, 0)
  const toggle = (campaignId: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(campaignId)) next.delete(campaignId)
      else next.add(campaignId)
      return next
    })
  }

  return (
    <div className={styles.spacing}>
      <Card className={styles.card} data-scope="inc-analytics-product-lens">
        <div className={styles.header}>
          <h3 className={styles.title}>
            {lensCopy.title}
            <span className={styles.subtitle}>{lensCopy.subtitle}</span>
          </h3>
          {action}
        </div>
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableHeader aria-label={lensCopy.expandProduct} />
              {lensColumns[lens].map((column) => <TableHeader className={column === lensColumns[lens][0] ? '' : styles.numericHeader} key={column}>{lensColumnTermKeys[column] ? <Term k={lensColumnTermKeys[column]} /> : column}</TableHeader>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {view.rows.map((row) => (
              <Fragment key={row.campaignId}>
                <ProductRow row={row} lens={lens} expanded={expanded.has(row.campaignId)} onToggle={() => toggle(row.campaignId)} />
                {expanded.has(row.campaignId) ? <ProductAdRows row={row} lens={lens} /> : null}
              </Fragment>
            ))}
            <TableRow className={styles.totalRow}>
              <TableCell />
              <TableCell><b>{lensCopy.total}</b></TableCell>
              <TableCell>{lensCopy.totalAds(totalAds)}</TableCell>
              {lens === 'sales' ? (
                <>
                  <TableCell numeric><b>{formatInteger(view.totals.units)}</b></TableCell>
                  <TableCell />
                  <TableCell numeric><b>{formatUsd(view.totals.revenue)}</b></TableCell>
                  <TableCell numeric><b>{formatUsd(view.totals.spend)}</b></TableCell>
                  <TableCell colSpan={2} />
                </>
              ) : (
                <>
                  <TableCell numeric><b>{formatInteger(view.totals.reach)}</b></TableCell>
                  <TableCell numeric><b>{formatInteger(view.totals.impressions)}</b></TableCell>
                  <TableCell />
                  <TableCell numeric><b>{formatUsd(view.totals.spend)}</b></TableCell>
                  <TableCell colSpan={2} />
                </>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  )
}
